// Admin-only "Other Reports" page.
//
// The menu is fetched from the server (script/ServerSideMenu.xml via the
// gateway), rendered as collapsible sections, and each action opens a
// parameter modal built from its <dialog>/<field> definition. Running a
// report downloads the generated XLS/PDF file (synchronous on ppServer).
//
// Desktop parity notes (see GetParameterDialog.cpp / ServerSideMenu.xml):
//  * date fields are sent as epoch MILLISECONDS at local midnight (desktop parity)
//  * combo fields send the option value token (may contain SQL fragments)
//  * select fields (lookup pickers) send "id:displayName"
//  * output format: PDF portrait / PDF landscape / Excel (.xls)

import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { api } from '../lib/api.js';
import type { ReportAction, ReportField, ReportLookupItem, ReportMenuNode } from '../lib/api.js';
import { TAB_UI_STYLES } from '../styles/tab-ui.js';

interface FlatReportItem {
  /** Submenu path within the top-level section (e.g. "By Cashier"). */
  group: string;
  action: ReportAction;
}

interface ReportSection {
  name: string;
  items: FlatReportItem[];
}

@customElement('reports-page')
export class ReportsPage extends LitElement {
  static styles = [TAB_UI_STYLES, css`
    :host {
      display: flex;
      flex-direction: column;
      height: 100%;
      min-height: 0;
      overflow: hidden;
    }
    .page-scroll { flex: 1; overflow-y: auto; min-height: 0; padding: 1rem; }
    .page-title { margin: 0 0 0.25rem; font-size: 1.1rem; }
    .subtitle { margin: 0 0 1rem; font-size: 0.78rem; color: var(--color-text-secondary); }
    .guard { text-align: center; padding: 3rem 1rem; color: var(--color-text-secondary); }

    .filter-row { margin-bottom: 1rem; }
    .filter-row input {
      width: 100%; box-sizing: border-box;
    }

    .rpt-section {
      border: 1px solid var(--color-border);
      border-radius: 14px;
      margin-bottom: 0.6rem;
      background: var(--color-surface);
      overflow: hidden;
    }
    .rpt-section-head {
      display: flex; align-items: center; justify-content: space-between;
      width: 100%; min-height: 48px; padding: 10px 14px; background: none; border: none;
      font-family: inherit; font-size: 0.88rem; font-weight: 600; cursor: pointer; color: var(--color-text);
      text-align: left;
    }
    .rpt-section-head:hover { background: #f4f7fb; }
    .rpt-section-head .chev { font-size: 0.7rem; color: var(--color-text-secondary); }
    .rpt-section-body { border-top: 1px solid var(--color-border); padding: 0.4rem 0.5rem 0.6rem; }
    .rpt-group-label {
      font-size: 0.68rem; font-weight: 700; text-transform: uppercase;
      letter-spacing: 0.04em; color: var(--color-text-secondary);
      padding: 0.5rem 0.6rem 0.2rem;
    }
    .rpt-item {
      display: block; width: 100%; text-align: left;
      min-height: 44px; padding: 10px 10px; margin: 0; background: none; border: none;
      border-radius: 10px; font-family: inherit; font-size: 0.84rem; cursor: pointer; color: var(--color-text);
    }
    .rpt-item:hover { background: #f4f7fb; color: var(--color-primary); }
    .empty-note { padding: 1rem; font-size: 0.8rem; color: var(--color-text-secondary); }

    /* ── Modal (self-contained; app-shell modals are shadow-scoped) ── */
    .modal-overlay {
      position: fixed; inset: 0; background: rgba(15,23,42,0.5);
      display: flex; align-items: center; justify-content: center;
      z-index: 200; padding: 1rem;
    }
    .modal {
      background: var(--color-surface); border-radius: 16px; width: 100%; max-width: 560px;
      max-height: 86dvh; display: flex; flex-direction: column; overflow: hidden;
      box-shadow: 0 24px 60px rgba(15,35,80,0.3);
    }
    .modal-header {
      display: flex; align-items: center; justify-content: space-between;
      padding: 0.9rem 1.1rem; border-bottom: 1px solid var(--color-border);
    }
    .modal-header h3 { margin: 0; font-size: 1rem; }
    .modal-close {
      display: flex; align-items: center; justify-content: center;
      width: 36px; height: 36px; background: none; border: none; border-radius: 10px;
      font-size: 1.2rem; line-height: 1; cursor: pointer; color: var(--color-text-secondary);
    }
    .modal-close:hover { background: #f4f7fb; color: var(--color-text); }
    .modal-body { padding: 1rem 1.1rem; overflow-y: auto; }
    .modal-footer {
      display: flex; justify-content: flex-end; gap: 0.5rem;
      padding: 0.8rem 1.1rem; border-top: 1px solid var(--color-border);
    }
    .dialog-title { margin: 0 0 0.75rem; font-size: 0.78rem; color: var(--color-text-secondary); }
    .fg { margin-bottom: 0.75rem; }
    .lookup-results {
      border: 1px solid var(--color-border); border-radius: 12px;
      margin-top: 0.3rem; max-height: 180px; overflow-y: auto;
    }
    .lookup-item {
      display: block; width: 100%; min-height: 44px; text-align: left; background: none;
      border: none; border-bottom: 1px solid var(--color-border);
      padding: 9px 12px; cursor: pointer;
    }
    .lookup-item:last-child { border-bottom: none; }
    .lookup-item:hover { background: #f4f7fb; }
    .lk-label { display: block; font-size: 0.82rem; }
    .lk-sub { display: block; font-size: 0.7rem; color: var(--color-text-secondary); }
    .lookup-picked { margin-top: 0.3rem; font-size: 0.72rem; color: var(--color-success, #188038); }
    .lookup-loading { margin-top: 0.3rem; font-size: 0.72rem; color: var(--color-text-secondary); }
    .progress-note {
      margin: 0.5rem 0 0; padding: 0.6rem 0.8rem; border-radius: 12px;
      background: #f0f4ff; font-size: 0.78rem; color: #1557b0;
    }
    .elapsed { font-weight: 600; margin-left: 0.4rem; }

    @media (max-width: 768px) {
      .modal-overlay { align-items: flex-end; padding: 0; }
      .modal { max-width: none; border-radius: 18px 18px 0 0; max-height: 92dvh; }
      .modal-footer { padding-bottom: calc(0.9rem + env(safe-area-inset-bottom)); }
    }
  `];

  @property({ type: String }) storeCode = '';

  @state() private _menu: ReportMenuNode | null = null;
  @state() private _loading = false;
  @state() private _error = '';
  @state() private _filter = '';
  @state() private _expanded: Record<string, boolean> = {};

  @state() private _selected: ReportAction | null = null;
  @state() private _values: Record<string, string> = {};
  @state() private _outputType: 'pdf' | 'pdfLandscape' | 'xls' = 'pdf';
  @state() private _running = false;
  @state() private _runError = '';
  @state() private _elapsed = 0;

  @state() private _lookupResults: Record<string, ReportLookupItem[]> = {};
  @state() private _lookupDisplay: Record<string, string> = {};
  @state() private _lookupLoading: Record<string, boolean> = {};

  private _timer: number | undefined;
  private _lookupTimers: Record<string, number> = {};

  connectedCallback() {
    super.connectedCallback();
    this._load();
  }

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null) {
    super.attributeChangedCallback(name, oldValue, newValue);
    if (name === 'storecode' && newValue && newValue !== oldValue) this._load();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this._timer !== undefined) window.clearInterval(this._timer);
    for (const id of Object.values(this._lookupTimers)) window.clearTimeout(id);
  }

  // ── Menu loading ──────────────────────────────────────────────────────

  private async _load() {
    if (!this.storeCode) return;
    this._loading = true;
    this._error = '';
    this._menu = null;
    try {
      const res = await api.getReportMenu(this.storeCode);
      if (!res.success || !res.data) throw new Error('Failed to load report menu');
      this._menu = res.data;
    } catch (e: any) {
      this._error = e?.message || 'Failed to load report menu';
    } finally {
      this._loading = false;
    }
  }

  /** Flatten all actions under a node, tracking the submenu path. */
  private _collect(node: ReportMenuNode, path: string[]): FlatReportItem[] {
    const out: FlatReportItem[] = [];
    const nextPath = [...path, node.name];
    for (const a of node.actions) {
      out.push({ group: nextPath.slice(1).join(' › '), action: a });
    }
    for (const m of node.menus) {
      out.push(...this._collect(m, nextPath));
    }
    return out;
  }

  private _sections(): ReportSection[] {
    if (!this._menu) return [];
    const sections: ReportSection[] = [];
    if (this._menu.actions.length > 0) {
      sections.push({
        name: 'General',
        items: this._menu.actions.map((a) => ({ group: '', action: a })),
      });
    }
    for (const m of this._menu.menus) {
      sections.push({ name: m.name, items: this._collect(m, []) });
    }
    return sections;
  }

  private _toggle(section: string) {
    this._expanded = { ...this._expanded, [section]: !this._expanded[section] };
  }

  private _matches(item: FlatReportItem): boolean {
    if (!this._filter) return true;
    const needle = this._filter.toLowerCase();
    return (
      item.action.name.toLowerCase().includes(needle) ||
      item.group.toLowerCase().includes(needle)
    );
  }

  // ── Dialog state ──────────────────────────────────────────────────────

  private _openAction(action: ReportAction) {
    const values: Record<string, string> = {};
    for (const f of action.fields) {
      if (f.type === 'date') values[f.param] = this._todayStr();
      else values[f.param] = f.defaultValue || '';
    }
    this._selected = action;
    this._values = values;
    this._outputType = 'pdf';
    this._runError = '';
    this._lookupResults = {};
    this._lookupDisplay = {};
  }

  private _closeDialog() {
    if (this._running) return;
    this._selected = null;
    this._runError = '';
  }

  private _setValue(param: string, value: string) {
    this._values = { ...this._values, [param]: value };
  }

  private _todayStr(): string {
    const d = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  }

  private _dateToEpochMs(dateStr: string): string {
    // Local midnight → epoch MILLISECONDS. Desktop parity: the report menu
    // dialog sends wxDateTime.GetValue() (internal m_time = ms) and ppServer
    // binds the whole number via sqlite3_bind_int64, so transactionDate
    // comparisons expect ms — seconds would silently match nothing.
    const t = new Date(`${dateStr}T00:00:00`).getTime();
    if (isNaN(t)) return '';
    return String(t);
  }

  private _parseComboOptions(list: string): Array<{ value: string; label: string }> {
    return list.split(',').map((entry) => {
      const idx = entry.indexOf(':');
      const value = idx === -1 ? entry : entry.substring(0, idx);
      const label = idx === -1 ? entry : entry.substring(idx + 1);
      return { value, label: label || value || '(All)' };
    });
  }

  // ── Lookup picker (select fields) ─────────────────────────────────────

  private _onLookupInput(field: ReportField, e: Event) {
    const q = (e.target as HTMLInputElement).value;
    this._lookupDisplay = { ...this._lookupDisplay, [field.param]: q };
    this._values = { ...this._values, [field.param]: '' };
    window.clearTimeout(this._lookupTimers[field.param]);
    this._lookupTimers[field.param] = window.setTimeout(
      () => this._doLookup(field, q), 300);
  }

  private async _doLookup(field: ReportField, q: string) {
    this._lookupLoading = { ...this._lookupLoading, [field.param]: true };
    try {
      const res = await api.searchReportLookup(this.storeCode, field.lookup, q, 30);
      this._lookupResults = {
        ...this._lookupResults,
        [field.param]: res.data?.results ?? [],
      };
    } catch {
      this._lookupResults = { ...this._lookupResults, [field.param]: [] };
    } finally {
      this._lookupLoading = { ...this._lookupLoading, [field.param]: false };
    }
  }

  private _selectLookup(field: ReportField, item: ReportLookupItem) {
    this._values = { ...this._values, [field.param]: `${item.id}:${item.label}` };
    this._lookupDisplay = { ...this._lookupDisplay, [field.param]: item.label };
    this._lookupResults = { ...this._lookupResults, [field.param]: [] };
  }

  // ── Run & download ────────────────────────────────────────────────────

  private async _run() {
    if (!this._selected || this._running) return;
    const action = this._selected;

    // Client-side guard for date fields (gateway rejects empty/non-numeric too).
    for (const f of action.fields) {
      if (f.type === 'date' && !this._values[f.param]) {
        this._runError = `Please select "${f.label}".`;
        return;
      }
    }

    this._running = true;
    this._runError = '';
    this._elapsed = 0;
    const start = Date.now();
    this._timer = window.setInterval(() => {
      this._elapsed = Math.floor((Date.now() - start) / 1000);
    }, 1000);

    try {
      const params = action.fields
        .filter((f) => f.param)
        .map((f) => ({
          param: f.param,
          value: f.type === 'date'
            ? this._dateToEpochMs(this._values[f.param] || '')
            : (this._values[f.param] ?? ''),
        }));

      const blob = await api.runReport(this.storeCode, {
        id: action.id,
        outputType: this._outputType,
        params,
      });

      this._download(blob, action);
      this._running = false;
      window.clearInterval(this._timer);
      this._timer = undefined;
      this._selected = null;
    } catch (e: any) {
      this._runError = e?.message || 'Report failed';
      this._running = false;
      window.clearInterval(this._timer);
      this._timer = undefined;
    }
  }

  private _download(blob: Blob, action: ReportAction) {
    const ext = this._outputType === 'xls' ? 'xls' : 'pdf';
    const base = action.name.replace(/[^a-z0-9]+/gi, '_').replace(/^_+|_+$/g, '') || 'report';
    const d = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    const stamp =
      `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}` +
      `_${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`;

    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${base}_${stamp}.${ext}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 2000);
  }

  // ── Rendering ─────────────────────────────────────────────────────────

  private _renderField(f: ReportField) {
    const value = this._values[f.param] ?? '';

    if (f.type === 'combo' && f.list) {
      const options = this._parseComboOptions(f.list);
      return html`
        <div class="fg">
          <label>${f.label}</label>
          <select ?disabled=${this._running}
            @change=${(e: Event) => this._setValue(f.param, (e.target as HTMLSelectElement).value)}>
            ${options.map((o) => html`
              <option value=${o.value} ?selected=${o.value === value}>${o.label}</option>
            `)}
          </select>
        </div>
      `;
    }

    if (f.type === 'select' && f.lookup) {
      const results = this._lookupResults[f.param] || [];
      return html`
        <div class="fg">
          <label>${f.label}</label>
          <input type="text" placeholder="Search…" ?disabled=${this._running}
            .value=${this._lookupDisplay[f.param] ?? ''}
            @input=${(e: Event) => this._onLookupInput(f, e)} />
          ${this._lookupLoading[f.param] ? html`<div class="lookup-loading">Searching…</div>` : ''}
          ${results.length > 0 ? html`
            <div class="lookup-results">
              ${results.map((item) => html`
                <button type="button" class="lookup-item"
                  @click=${() => this._selectLookup(f, item)}>
                  <span class="lk-label">${item.label || item.id}</span>
                  ${item.columns.slice(2, 4).filter((c) => !!c).length > 0 ? html`
                    <span class="lk-sub">${item.columns.slice(2, 4).filter((c) => !!c).join(' · ')}</span>
                  ` : ''}
                </button>
              `)}
            </div>
          ` : ''}
          ${value ? html`
            <div class="lookup-picked">Selected: ${value.includes(':') ? value.substring(value.indexOf(':') + 1) : value}</div>
          ` : ''}
        </div>
      `;
    }

    if (f.type === 'date') {
      return html`
        <div class="fg">
          <label>${f.label}</label>
          <input type="date" ?disabled=${this._running} .value=${value}
            @change=${(e: Event) => this._setValue(f.param, (e.target as HTMLInputElement).value)} />
        </div>
      `;
    }

    if (f.type === 'integer') {
      return html`
        <div class="fg">
          <label>${f.label}</label>
          <input type="number" step="1" ?disabled=${this._running} .value=${value}
            @input=${(e: Event) => this._setValue(f.param, (e.target as HTMLInputElement).value)} />
        </div>
      `;
    }

    if (f.type === 'number') {
      return html`
        <div class="fg">
          <label>${f.label}</label>
          <input type="number" step="any" ?disabled=${this._running} .value=${value}
            @input=${(e: Event) => this._setValue(f.param, (e.target as HTMLInputElement).value)} />
        </div>
      `;
    }

    // string / text / unknown
    return html`
      <div class="fg">
        <label>${f.label}</label>
        <input type="text" ?disabled=${this._running} .value=${value}
          @input=${(e: Event) => this._setValue(f.param, (e.target as HTMLInputElement).value)} />
      </div>
    `;
  }

  private _renderDialog() {
    const action = this._selected;
    if (!action) return '';
    return html`
      <div class="modal-overlay" @click=${() => this._closeDialog()}>
        <div class="modal" @click=${(e: Event) => e.stopPropagation()}>
          <div class="modal-header">
            <h3>${action.name}</h3>
            <button class="modal-close" @click=${() => this._closeDialog()}>×</button>
          </div>
          <div class="modal-body">
            ${action.dialogTitle ? html`<p class="dialog-title">${action.dialogTitle}</p>` : ''}
            ${action.fields.map((f) => this._renderField(f))}
            <div class="fg">
              <label>Output Format</label>
              <select ?disabled=${this._running}
                @change=${(e: Event) => {
                  this._outputType = (e.target as HTMLSelectElement).value as any;
                }}>
                <option value="pdf" ?selected=${this._outputType === 'pdf'}>PDF (Portrait)</option>
                <option value="pdfLandscape" ?selected=${this._outputType === 'pdfLandscape'}>PDF (Landscape)</option>
                <option value="xls" ?selected=${this._outputType === 'xls'}>Excel (.xls)</option>
              </select>
            </div>
            ${this._runError ? html`<div class="err">${this._runError}</div>` : ''}
            ${this._running ? html`
              <div class="progress-note">
                Generating report… larger date ranges can take a while.
                <span class="elapsed">${this._elapsed}s</span>
              </div>
            ` : ''}
          </div>
          <div class="modal-footer">
            <button class="btn-cancel" ?disabled=${this._running}
              @click=${() => this._closeDialog()}>Cancel</button>
            <button class="btn-submit" ?disabled=${this._running}
              @click=${() => this._run()}>${this._running ? 'Running…' : 'Run & Download'}</button>
          </div>
        </div>
      </div>
    `;
  }

  private _renderSection(section: ReportSection) {
    const filtering = !!this._filter;
    const items = filtering ? section.items.filter((i) => this._matches(i)) : section.items;
    if (filtering && items.length === 0) return '';

    // While filtering keep sections open so matches are visible.
    const open = filtering || this._expanded[section.name];

    let lastGroup = '';
    return html`
      <div class="rpt-section">
        <button class="rpt-section-head" @click=${() => this._toggle(section.name)}>
          <span>${section.name}</span>
          <span class="chev">${open ? '▾' : '▸'}</span>
        </button>
        ${open ? html`
          <div class="rpt-section-body">
            ${items.map((item) => {
              const showGroup = item.group && item.group !== lastGroup;
              lastGroup = item.group;
              return html`
                ${showGroup ? html`<div class="rpt-group-label">${item.group}</div>` : ''}
                <button class="rpt-item" @click=${() => this._openAction(item.action)}>
                  ${item.action.name}
                </button>
              `;
            })}
          </div>
        ` : ''}
      </div>
    `;
  }

  render() {
    if (!this.storeCode) {
      return html`
        <div class="guard">
          <h3>📊 Reports</h3>
          <p>Select an outlet from the sidebar to run reports.</p>
        </div>
      `;
    }

    const total = this._sections().reduce((n, s) => n + s.items.length, 0);

    return html`
      <div class="page-scroll">
        <h2 class="page-title">📊 Other Reports</h2>
        <p class="subtitle">
          Admin-only server reports (XLS / PDF). Menu loaded from
          ServerSideMenu.xml${total ? html` — ${total} reports` : ''}.
        </p>

        ${this._loading ? html`<div class="empty-note">Loading report menu…</div>` : ''}
        ${this._error ? html`<div class="err">${this._error}</div>` : ''}

        ${!this._loading && !this._error ? html`
          <div class="filter-row">
            <input type="text" placeholder="Filter reports…" .value=${this._filter}
              @input=${(e: Event) => { this._filter = (e.target as HTMLInputElement).value; }} />
          </div>
          ${this._sections().map((s) => this._renderSection(s))}
        ` : ''}
      </div>

      ${this._renderDialog()}
    `;
  }
}
